import { Box, Divider, HStack } from "@chakra-ui/react";
import type {
  TaskAssigneesProps,
  TaskCurrentAssigneeAvatarProps,
} from "@components/modules/resource-planning/tasks/components/task-assignees/types";
import {
  hasProfileTooltip,
  toAvatarStackItem,
} from "@components/modules/resource-planning/tasks/components/task-assignees/utils";
import { Avatar } from "@components/ui-kit/avatar";
import { AvatarPlaceholder } from "@components/ui-kit/avatar/variants/placeholder";
import { AvatarStack } from "@components/ui-kit/avatar/variants/stack";
import { UserTooltip } from "@components/ui-kit/UserTooltip";
import { t } from "@lingui/macro";

/** Task assignee avatars, read-only. */
export const TaskAssignees = ({
  assignees,
  currentAssignee,
  isTooltipDisabled,
}: TaskAssigneesProps) => (
  <HStack>
    {currentAssignee !== undefined && (
      <>
        <TaskCurrentAssigneeAvatar
          user={currentAssignee}
          placeholderLabel={t`No current assignee`}
          isTooltipDisabled={isTooltipDisabled}
        />
        {assignees.length > 0 && <TaskAssigneesDivider />}
      </>
    )}
    <AvatarStack
      items={assignees.map(toAvatarStackItem)}
      isTooltipDisabled={isTooltipDisabled}
      renderAvatar={(avatar, item) => (
        <UserTooltip
          user={assignees.find(({ id }) => id === item.id)}
          isDisabled={isTooltipDisabled}
        >
          {avatar}
        </UserTooltip>
      )}
    />
  </HStack>
);

/** The highlighted current assignee, or a dashed avatar when nobody is. */
export const TaskCurrentAssigneeAvatar = ({
  user,
  placeholderLabel,
  isTooltipDisabled,
}: TaskCurrentAssigneeAvatarProps) => {
  if (!user) {
    return (
      <AvatarPlaceholder
        label={placeholderLabel}
        isTooltipDisabled={isTooltipDisabled}
        isHighlighted
      />
    );
  }

  return (
    <UserTooltip user={user} isDisabled={isTooltipDisabled}>
      <Avatar
        name={user.full_name}
        src={user.image?.urls.thumbnail}
        colorScheme={user.profile?.hex_color}
        tooltip={hasProfileTooltip(user) ? undefined : user.full_name}
        isTooltipDisabled={isTooltipDisabled}
        isHighlighted
      />
    </UserTooltip>
  );
};

export const TaskAssigneesDivider = () => (
  <Box h="20px">
    <Divider orientation="vertical" />
  </Box>
);
